/**
 * Gestion › Enfants › Modifier : la fiche d'un enfant, corrigée par l'équipe.
 *
 * Depuis le 26/10/2026, l'écran pose les questions du formulaire des parents,
 * dans ses cinq parties, et écrit les mêmes colonnes avec les mêmes règles :
 * cinq autorisations d'image au lieu de l'ancienne case photo, l'accord de
 * soins avec les autorisations, toutes les écoles et « autre école », la
 * classe de l'année, le niveau de natation, les personnes autorisées. Ce qui
 * change pour l'équipe est dans `ficheEnfant/regles.ts`.
 *
 * Chaque onglet s'enregistre seul et n'écrit que ce qui a changé ; jamais
 * `last_verified_at` — seule la vérification par le parent déclare la fiche à
 * jour.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowLeft, Save, Loader2, CheckCircle, AlertCircle, RotateCcw } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { db } from '../../../lib/payments/db'
import { VERSION_POLITIQUE } from '../../../lib/confidentialite'
import { anneeScolaireCourante, type ClassesConnues } from '../../../lib/classes/regles'
import { enregistrerClasse, lireClassesConnues } from '../../../lib/classes/gateway.supabase'
import { ecoleEnregistree, type ContexteFiche, type Fiche } from '../../../components/forms/kid/fiche'
import {
  ONGLETS,
  anciennesReponses,
  apresEnregistrement,
  controleEquipe,
  ecrituresEquipe,
  ongletModifie,
  type Onglet,
  type ReperesEquipe,
} from './ficheEnfant/regles'
import {
  OngletAutorisations,
  OngletIdentite,
  OngletJournee,
  OngletSante,
  OngletSorties,
  type Ecole,
  type ReponsesImage,
} from './ficheEnfant/Onglets'
import { lireFicheEquipe, type LectureFiche } from './ficheEnfant/lecture'
import { NiveauDAcces, type NiveauDAccesLu } from '../NiveauDAcces'

interface KidsEditFormProps {
  kidId: string
  onBack: () => void
  /** L'onglet ouvert d'abord : « Journée en stage » depuis Gestion › Inclusion. */
  ongletInitial?: Onglet
}

/** Le nom d'une table dans un message d'échec. */
const TABLE_LISIBLE: Record<string, string> = {
  kid_health: 'la santé',
  kid_allergies: 'les allergies et le régime',
  kid_activities: 'les activités',
  kid_inclusion: 'les besoins particuliers',
  kid_departure: 'les sorties',
  kid_image_rights: 'les autorisations d’image',
}

export function KidsEditForm({ kidId, onBack, ongletInitial = 'identite' }: KidsEditFormProps) {
  const annee = useMemo(() => anneeScolaireCourante(), [])
  const [onglet, setOnglet] = useState<Onglet>(ongletInitial)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  const [lecture, setLecture] = useState<LectureFiche | null>(null)
  const [brouillon, setBrouillon] = useState<Fiche | null>(null)
  const [reperes, setReperes] = useState<ReperesEquipe>({ formulaireMedication: false })
  const [reponsesDuParent, setReponsesDuParent] = useState(false)
  const [ecoles, setEcoles] = useState<Ecole[]>([])
  /* Les classes des écoles reliées à un centre. Sans elles, la classe n'est pas demandée et jamais effacée. */
  const [classes, setClasses] = useState<ClassesConnues | undefined>(undefined)

  useEffect(() => {
    let annule = false
    setLoading(true)
    Promise.all([
      lireFicheEquipe(kidId, annee),
      db.from('schools').select('id, name, code_postal, active').order('name'),
      lireClassesConnues().catch(() => undefined),
    ])
      .then(([l, ecolesRes, connues]) => {
        if (annule) return
        setLecture(l)
        setBrouillon(l.fiche)
        setReperes(l.reperes)
        setEcoles((ecolesRes.data ?? []) as Ecole[])
        setClasses(connues)
      })
      .catch(err => {
        console.error('Error fetching kid data:', err)
        if (!annule) setFeedback({ type: 'error', message: 'Erreur lors du chargement de la fiche.' })
      })
      .finally(() => {
        if (!annule) setLoading(false)
      })
    return () => {
      annule = true
    }
  }, [kidId, annee])

  useEffect(() => {
    if (feedback?.type === 'success') {
      const timer = setTimeout(() => setFeedback(null), 4000)
      return () => clearTimeout(timer)
    }
  }, [feedback])

  const contexte = useMemo<ContexteFiche>(
    () => ({
      nrnInterdits: [lecture?.parent?.nnational ?? '', ...(lecture?.nrnFratrie ?? [])].filter(Boolean),
      classes,
    }),
    [lecture, classes],
  )

  const depart = lecture?.fiche ?? null
  const controles = useMemo(() => {
    if (!brouillon || !depart) return null
    return Object.fromEntries(
      ONGLETS.map(({ cle }) => [cle, controleEquipe(cle, brouillon, depart, contexte, { reponsesDuParent })]),
    ) as Record<Onglet, ReturnType<typeof controleEquipe>>
  }, [brouillon, depart, contexte, reponsesDuParent])

  const changerOnglet = (cle: Onglet) => {
    setOnglet(cle)
    setFeedback(null)
  }

  const handleCancel = () => {
    if (!brouillon || !lecture) return
    setBrouillon({ ...brouillon, [onglet]: lecture.fiche[onglet] })
    if (onglet === 'sante') setReperes(lecture.reperes)
    if (onglet === 'autorisations') setReponsesDuParent(false)
    setFeedback(null)
  }

  const handleSave = useCallback(async () => {
    if (!brouillon || !lecture || !controles || saving) return
    const bloquantes = Object.keys(controles[onglet].bloquantes).length
    if (bloquantes > 0) {
      setFeedback({
        type: 'error',
        message: bloquantes === 1
          ? 'Une information est à corriger avant d’enregistrer, signalée en rouge.'
          : `${bloquantes} informations sont à corriger avant d’enregistrer, signalées en rouge.`,
      })
      return
    }

    setSaving(true)
    setFeedback(null)
    try {
      const { data: session } = await supabase.auth.getSession()
      const auteurId = session.session?.user.id ?? null
      const ecritures = ecrituresEquipe(
        onglet, brouillon, lecture.fiche, reperes, lecture.reperes, kidId, auteurId, VERSION_POLITIQUE,
      )
      let rien = !ecritures.kids && ecritures.lignes.length === 0

      if (ecritures.kids) {
        const { error } = await db.from('kids').update(ecritures.kids).eq('id', kidId)
        if (error) throw new Error(`l’identité n’a pas pu être enregistrée (${error.message})`)
      }
      for (const { table, ligne } of ecritures.lignes) {
        const { error } = await db.from(table).upsert(ligne, { onConflict: 'kid_id' })
        if (error) throw new Error(`${TABLE_LISIBLE[table] ?? table} : ${error.message}`)
      }

      if (onglet === 'identite' && classes) {
        /*
          La classe de l'année, dans `kid_classes`, quand l'école ou la classe a
          changé : écrite si elle est choisie, retirée sinon (une école sans
          classes, une école tapée à la main). Corriger une adresse ne touche
          jamais à la classe.
        */
        const ecole = ecoleEnregistree(brouillon.identite)
        const ecoleChange = ecole !== ecoleEnregistree(lecture.fiche.identite)
        const classeChange = brouillon.identite.classeId !== lecture.fiche.identite.classeId
        if (ecoleChange || classeChange) {
          try {
            await enregistrerClasse(kidId, ecole, ecole, annee, brouillon.identite.classeId)
            rien = false
          } catch (err) {
            const raison = err instanceof Error ? err.message : String(err)
            throw new Error(`l’identité est enregistrée, mais pas la classe (${raison})`)
          }
        }
      }

      const relue = await lireFicheEquipe(kidId, annee).catch(() => null)
      if (!relue) {
        setFeedback({ type: 'error', message: 'Modifications enregistrées, mais la fiche n’a pas pu être relue : rechargez la page.' })
        return
      }
      setBrouillon(apresEnregistrement(brouillon, lecture.fiche, relue.fiche, onglet))
      if (onglet === 'sante') setReperes(relue.reperes)
      if (onglet === 'autorisations') setReponsesDuParent(false)
      setLecture(relue)
      setFeedback(rien
        ? { type: 'success', message: 'Rien n’a changé.' }
        : { type: 'success', message: `${ONGLETS.find(o => o.cle === onglet)?.titre} : modifications enregistrées.` })
    } catch (err) {
      const raison = err instanceof Error ? err.message : String(err)
      setFeedback({ type: 'error', message: `L’enregistrement a échoué : ${raison}. Les réponses saisies sont conservées.` })
    } finally {
      setSaving(false)
    }
  }, [brouillon, lecture, controles, saving, onglet, reperes, kidId, classes, annee])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    )
  }

  if (!lecture || !brouillon || !controles) {
    return (
      <div className="space-y-4">
        <button onClick={onBack} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="text-center py-12 text-gray-500">{feedback?.message ?? 'Enfant introuvable.'}</div>
      </div>
    )
  }

  const { parent } = lecture
  const enfant = lecture.fiche.identite.prenom.trim() || 'L’enfant'
  const verifieeLe = lecture.lignes.kids?.last_verified_at
  const aCompleter = ONGLETS.reduce((n, { cle }) => n + Object.keys(controles[cle].manques).length, 0)
  const messages = controles[onglet]
  const modifie = ongletModifie(onglet, brouillon, lecture.fiche, reperes, lecture.reperes)
  const imageModifiee = JSON.stringify(brouillon.autorisations.image) !== JSON.stringify(lecture.fiche.autorisations.image)
  const anciennes = anciennesReponses(lecture.lignes)
  const maj = <K extends Onglet>(cle: K) => (v: Fiche[K]) => setBrouillon(prev => (prev ? { ...prev, [cle]: v } : prev))
  const adresseParent = parent?.adresse?.trim() && parent.cpostal?.trim() && parent.localite?.trim()
    ? { adresse: parent.adresse.trim(), cPostal: parent.cpostal.trim(), localite: parent.localite.trim() }
    : null

  return (
    <div className="space-y-4">
      {/* En-tête */}
      <div className="flex items-start space-x-3">
        <button onClick={onBack} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">
            {lecture.fiche.identite.prenom} {lecture.fiche.identite.nom}
          </h1>
          <p className="text-sm text-gray-500">
            Parent : {parent?.prenom} {parent?.nom}{parent?.email ? ` (${parent.email})` : ''}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            {verifieeLe
              ? `Fiche vérifiée par le parent le ${new Date(verifieeLe).toLocaleDateString('fr-BE')}.`
              : 'Fiche jamais vérifiée par le parent avec le formulaire actuel.'}
            {aCompleter > 0 && (
              <span className="text-amber-700">
                {' '}{aCompleter === 1 ? 'Une information manque' : `${aCompleter} informations manquent`} : le parent les complétera à sa prochaine vérification.
              </span>
            )}
          </p>
        </div>
      </div>

      {feedback && (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium ${
          feedback.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {feedback.type === 'success' ? <CheckCircle className="h-4 w-4 flex-shrink-0" /> : <AlertCircle className="h-4 w-4 flex-shrink-0" />}
          {feedback.message}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex min-w-max">
            {ONGLETS.map(({ cle, titre }) => {
              const actif = onglet === cle
              const manques = Object.keys(controles[cle].manques).length + Object.keys(controles[cle].bloquantes).length
              const enCours = ongletModifie(cle, brouillon, lecture.fiche, reperes, lecture.reperes)
              return (
                <button
                  key={cle}
                  onClick={() => changerOnglet(cle)}
                  className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    actif
                      ? 'border-amber-600 text-amber-700 bg-amber-50/50'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {titre}
                  {enCours && <span className="h-2 w-2 rounded-full bg-amber-500" title="Modifications non enregistrées" />}
                  {manques > 0 && (
                    <span className="rounded-full bg-amber-100 px-1.5 text-xs font-semibold text-amber-800" title="Informations à compléter">
                      {manques}
                    </span>
                  )}
                </button>
              )
            })}
          </nav>
        </div>

        <div className="p-4 sm:p-6">
          {onglet === 'identite' && (
            <OngletIdentite
              valeur={brouillon.identite} onChange={maj('identite')} messages={messages} enfant={enfant}
              ecoles={ecoles} classes={classes} annee={annee}
              ecoleEnregistree={lecture.fiche.identite.ecole}
              classeEnregistree={lecture.classe?.id ?? ''} adresseParent={adresseParent}
            />
          )}
          {onglet === 'sante' && (
            <OngletSante
              valeur={brouillon.sante} onChange={maj('sante')} messages={messages} enfant={enfant}
              reperes={reperes} onReperes={setReperes}
            />
          )}
          {onglet === 'vie' && (
            <OngletJournee
              valeur={brouillon.vie} onChange={maj('vie')} messages={messages} enfant={enfant} anciennes={anciennes}
              decisionInclusion={!(lecture.fiche.vie.besoinsSpecifiques || lecture.lignes.inclusion?.niveau_acces != null) ? null : (
                <div className="space-y-3">
                  {lecture.fiche.vie.besoinsSpecifiques && !brouillon.vie.besoinsSpecifiques && (
                    /*
                      Décocher pour laisser un enfant s'inscrire le faisait sortir du
                      rapport ONE et des fiches d'accueil (19 enfants au 25/10/2026).
                    */
                    <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
                      Décochée, la case fait sortir {enfant} du rapport ONE et des fiches d’accueil. Pour que ses
                      parents l’inscrivent sans demande d’inclusion, gardez-la cochée et choisissez le niveau 1.
                    </p>
                  )}
                  <NiveauDAcces
                    kidId={kidId}
                    prenom={enfant}
                    decision={(lecture.lignes.inclusion ?? {}) as NiveauDAccesLu}
                    onDecide={decision => setLecture(prev => prev && {
                      ...prev, lignes: { ...prev.lignes, inclusion: { ...prev.lignes.inclusion, ...decision } },
                    })}
                  />
                </div>
              )}
            />
          )}
          {onglet === 'sorties' && (
            <OngletSorties
              valeur={brouillon.sorties} onChange={maj('sorties')} messages={messages} enfant={enfant}
              personnes={lecture.personnes} anciennes={anciennes}
            />
          )}
          {onglet === 'autorisations' && (
            <OngletAutorisations
              valeur={brouillon.autorisations} onChange={maj('autorisations')} messages={messages} enfant={enfant}
              parentId={lecture.lignes.kids?.user_id ?? ''}
              reponsesImage={(lecture.lignes.image_rights as ReponsesImage | null | undefined) ?? null}
              anciennes={anciennes}
              imageModifiee={imageModifiee}
              reponsesDuParent={reponsesDuParent}
              onReponsesDuParent={setReponsesDuParent}
              journal={lecture.journal}
            />
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-4 sm:px-6 py-4 border-t border-gray-200 bg-gray-50">
          {modifie && <span className="mr-auto text-xs text-amber-700">Modifications non enregistrées</span>}
          <button
            onClick={handleCancel}
            disabled={!modifie || saving}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            <RotateCcw className="h-4 w-4" />
            Annuler
          </button>
          <button
            onClick={() => void handleSave()}
            disabled={saving || !modifie}
            className="flex items-center gap-1.5 px-5 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  )
}
